<template>
  <VDialogV3
    id="bank-account-card"
    position="right"
    width="600px"

    :entity="localBankAccount"
    :loading="false"
    :submit-disabled="isCreateMode ? null : 'Расчётный счёт нельзя редактировать'"

    @submit="onSubmit"
  >
    <section>
      <VSelect
        ref="company"
        v-model="localBankAccount.company"
        :disabled="!isCreateMode"
        required
        label="Юр. лицо"
        layout="vertical"
        :options="partner?.companies"
      />
    </section>

    <section>
      <VFormItem
        label="Расчётный счёт"
        layout="vertical"
        with-asterisk
      >
        <VInputMask
          ref="accountInput"
          :value="localBankAccount.account"
          :mask="localBankAccount.getAccountMask()"
          required
          :validate-method="() => localBankAccount.validateAccountNumber()"
          :disabled="!isCreateMode"
          @input="localBankAccount.account = $event"
        />

        <VSelect
          :value="currencyCode"
          :options="currencies"
          option-label="symbol"
          option-value="code"
          :disabled="!isCreateMode"
          @input="onInputCurrencyCode"
        />
      </VFormItem>

      <VInputMask
        ref="rcbicInput"
        :label="localBankAccount.getBankIdLabel()"
        layout="vertical"
        :mask="localBankAccount.getBankIdMask()"
        required
        :value="localBankAccount.bank.id"
        :disabled="!isCreateMode"
        @input="onBankIdInput"
      />

      <VInput
        ref="swift"
        v-model="localBankAccount.swift"
        label="SWIFT"
        layout="vertical"
        :disabled="!isCreateMode"
        :validate-method="(value) => localBankAccount.validateSwift(value)"
      />
    </section>

    <!-- реквизиты банка -->
    <section v-if="localBankAccount.bank?.name">
      <VInput
        label="Наименование банка"
        layout="vertical"
        :value="localBankAccount.bank.name"
        disabled
      />
      <VInput
        label="Корреспондентский счёт"
        layout="vertical"
        :value="localBankAccount.bank.account"
        :disabled="!isCreateMode"
      />
      <VInput
        label="Налоговый номер"
        layout="vertical"
        :value="localBankAccount.bank.tin"
        disabled
      />
      <VTextarea
        v-model="localBankAccount.bank.address.stringRepresentation"
        label="Адрес"
        layout="vertical"
        disabled
      />
      <VTextarea
        v-model="localBankAccount.bank.address.cityName"
        label="Город"
        layout="vertical"
        disabled
      />
    </section>

    <section v-if="isCreateMode">
      <VHelperText icon="attention">
        Пожалуйста, будьте внимательны при заполнении.<br>
        После создания счёта его редактирование невозможно, но вы можете воспользоваться функцией удаления.
      </VHelperText>
    </section>
  </VDialogV3>
</template>

<script lang="ts">

import BankAccount         from "@/domains/BankAccounts/BankAccount";
import BankAccountsService from "@/domains/BankAccounts/BankAccountsService";
import CurrenciesService   from "@/domains/Currencies/CurrenciesService";
import BanksService        from "@/domains/Banks/BanksService";
import Partner             from "@/domains/Partners/Partner";

export default defineComponent({
  name: 'BankAccountCard',

  props: {
    bankAccount: {
      type:    BankAccount,
      default: null,
    },
    partner: {
      type:     Partner,
      required: true,
    },
  },

  emits: [
    BankAccount.Events.Created,
  ],

  data() {
    return {
      localBankAccount: this.$core.utils.cloneDeep(this.bankAccount) as BankAccount,
    };
  },

  computed: {
    currencies() {
      return Array.from(this.$core.currencies.currencies.values());
    },

    isCreateMode() {
      return !this.localBankAccount.id;
    },

    currencyCode: {
      get() {
        return CurrenciesService.currencies.get(this.localBankAccount.currencyCode);
      },
      set(value) {
        this.localBankAccount.currencyCode = value;
      },
    },
  },

  methods: {
    onInputCurrencyCode(value) {
      this.currencyCode = value.code;
    },

    async onBankIdInput(value) {
      const id = typeof value === 'string' ? value : value?.raw ?? '';

      this.localBankAccount.bank.id = id;

      const completeLength = this.localBankAccount.getBankIdCompleteLength();

      if (completeLength !== null && id.length === completeLength) {
        const bank = await BanksService.getBank(this.localBankAccount.countryCode, id);

        if (bank) {
          this.localBankAccount.bank = bank;
        }
      }
    },

    /**
     * Сабмит. Валидацию формы делает сам VDialogV3 — @submit приходит уже валидным.
     * Здесь остаётся только доменная проверка уникальности (диалог про неё не знает)
     * и создание счёта.
     */
    async onSubmit() {
      if (this.localBankAccount.isAccountDuplicate()) {
        this.$core.showNotification.error(
          `Ошибка уникальности. Расчётный счёт с указанными юр. лицом, номером счёта и ${this.localBankAccount.getBankIdLabel()} уже существует.`
        );
        return;
      }

      const created = await BankAccountsService.add(
        this.localBankAccount.company.id,
        this.localBankAccount,
      );

      if (created) {
        this.$emit(BankAccount.Events.Created, this.localBankAccount);
        this.closeDialog();
      }
    },

    closeDialog() {
      this.$core.dialogs.BankAccountCard.hide();
    },
  },
});
</script>
